import { ConfigProvider, Form, Input, message, Modal, Select, Button } from "antd";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import container from "@/lib/di/container";
import React from "react";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import { SUSPENSION_STATUSES, suspensionStatusPipe } from "@/core/pipes/suspension-status.pipe";
import { FeeLadderModel } from "@/core/models/fee-ladder.model";
import { FeeLadderRepo } from "@/core/repos/tables/fee-ladder.repo";
import { FEE_LADDER_TYPES, feeLadderTypePipe } from "@/core/pipes/fee-ladder-type.pipe";
import { INVOICE_DATE_TYPES, invoiceDateTypePipe } from "@/core/pipes/invoice-date-type.pipe";
import { Province } from "@/core/models/address/province.model";
import { AddressRepo } from "@/core/repos/address.repo";
import { usePermissionStore } from "@/core/stores/permission.store";
import { SaveButton } from "@/app/home/components/table/SaveButton";
import { SyncOutlined } from '@ant-design/icons';
import { useDrafts } from '@/core/stores/drafts.store';
import { ResetDraftButton } from '@/app/home/components/drafts/ResetDraftButton';

const addressRepo = container.get(AddressRepo);
const feeLadderRepo = container.get(FeeLadderRepo);

interface DetailModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: FeeLadderModel,
}

const INPUT_WIDTH = 450;

export const DetailModal: React.FC<DetailModalProps> = ({isModalOpen, setIsModalOpen, data}) => {
    const permissionStore = usePermissionStore();
    const [form] = Form.useForm<FeeLadderModel>();
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [messageApi, contextHolder] = message.useMessage();   
    const [provinces, setProvinces] = useState<Province[]>([]);
    // Draft logic for FeeLadderModel
    const draft = useDrafts((state) => state.getDraft('FeeLadderModel'));
    const _saveDraft = useDrafts((state) => state.saveDraft);
    const saveDraft = () => _saveDraft('FeeLadderModel', form.getFieldsValue());
    const _clearDraft = useDrafts((state) => state.clearDraft);
    const clearDraft = () => {
        _clearDraft('FeeLadderModel');
        form.setFieldsValue(data);
    };
    // Attach hooks to handle data changes
    const handleDataChange = async () => {
        form.setFieldsValue(data);
        if (data.id && data.id.length > 0) {
            setIsEdit(true);
        } else {
            if (draft) form.setFieldsValue(draft);
            setIsEdit(false);
        }
    }
    useEffect(() => {
        handleDataChange();
    }, [data])

    const populateSelections = async () => {
        const _provinces = (await addressRepo.getAllProvinces()).map(data => data[1]);
        setProvinces(_provinces);
    } 

    // Initialize select options data
    const init = async () => {
        await populateSelections();
    }

    // componentDidMount equivalent, run exclusively on client-side
    useEffect(() => {init()}, []);


    const handleSave = async () => {
        try {
            setLoading(true);
            const formData = await form.validateFields();
            if (isEdit) {
                formData.id = data.id;
                const result = await feeLadderRepo.update({...data, ...formData});
                setIsModalOpen(false);
                if (result) {
                    messageApi.success({
                        content: "Đã cập nhật bản ghi"
                    })
                } else {
                    messageApi.error({
                        content: "Cập nhật bản ghi thất bại"
                    })
                }
            } else {
                const result = await feeLadderRepo.create({...data, ...formData});
                setIsModalOpen(false);
                clearDraft();
                if (result) {
                    messageApi.success({
                        content: "Thêm bản ghi mới thành công"
                    })
                } else {
                    messageApi.error({
                        content: "Thêm bản ghi mới thất bại"
                    })
                }
            }
        } catch (error) {
            console.log(error);
        }
        setLoading(false);
    };
    const handleCancel = () => {
      if (!isEdit) saveDraft();
      setIsModalOpen(false);
    };

    return (
        <ConfigProvider theme={MODAL_THEME}>
            {contextHolder}
            <Modal open={isModalOpen} onCancel={handleCancel} width={980} centered
            styles={{
                mask: {
                    backdropFilter: 'blur(2px)',
                    backgroundColor: 'var(--primary-mask)'
                }
            }}
            footer={[
                <div key={1} className="w-full flex flex-row justify-end px-1.5">
                    <SaveButton isEdit={isEdit} loading={loading} onClick={handleSave}/>
                </div>,
            ]}>
                <div className="flex flex-row w-full items-center justify-start select-none">
                    <p className="ml-2 text-2xl font-bold text-primary">CHI TIẾT BẢNG GIÁ</p>
                    {!isEdit && <ResetDraftButton onClick={clearDraft} />}
                </div>
                <div className="flex flex-row justify-center mt-6">
                    <Form<FeeLadderModel>
                        name="basic"
                        className="flex flex-col items-end"
                        form={form}
                        layout="vertical"
                        disabled={isEdit&&!permissionStore.page.edit}
                    >
                        <div className="grid grid-cols-2 gap-x-4 items-end">
                            <Form.Item<FeeLadderModel>
                                label="Tên"
                                name="name"
                                rules={[{ required: true, message: 'Bạn phải chọn Tên!' }]}
                            >
                                <Input placeholder="Nhập tên" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>

                            <Form.Item<FeeLadderModel>
                                label="Phí BVMT (%)"
                                name="enviromentFee"
                            >
                                <Input placeholder="Nhập phí BVMT" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>

                            <Form.Item<FeeLadderModel>
                                label="Tỉnh/Thành phố trực thuộc"
                                name="provinceId"
                                rules={[{ required: true, message: 'Bạn phải chọn Tỉnh/Thành phố trực thuộc!' }]}
                            >
                                <Select placeholder="Chọn" style={{width: INPUT_WIDTH}} disabled={isEdit}>
                                    {provinces.map(province => <Select.Option key={province.id} value={province.id}>{province.name}</Select.Option>)}
                                </Select>
                            </Form.Item>

                            <Form.Item<FeeLadderModel>
                                label="Phí giá trị gia tăng (%)"
                                name="vat"
                            >
                                <Input placeholder="Nhập phí giá trị gia tăng" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>

                            <Form.Item<FeeLadderModel>
                                label="Trạng thái"
                                name="status"
                                rules={[{ required: true, message: 'Bạn phải chọn Trạng thái!' }]}
                            >
                                <Select showSearch placeholder="Chọn" style={{width: INPUT_WIDTH}}>
                                    {SUSPENSION_STATUSES.map(type => <Select.Option key={type} value={type}>{suspensionStatusPipe(type)}</Select.Option>)}
                                </Select>
                            </Form.Item>

                            <Form.Item<FeeLadderModel>
                                label="Chỉ số tính giá sàn"
                                name="floorIndex"
                            >
                                <Input placeholder="Nhập chỉ số tính giá sàn" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>

                            <Form.Item<FeeLadderModel>
                                label="Loại thời gian xuất hóa đơn"
                                name="invoiceDateType"
                                rules={[{ required: true, message: 'Bạn phải chọn Loại thời gian xuất hóa đơn!' }]}
                            >
                                <Select showSearch placeholder="Chọn" style={{width: INPUT_WIDTH}}>
                                    {INVOICE_DATE_TYPES.map(type => <Select.Option key={type} value={type}>{invoiceDateTypePipe(type)}</Select.Option>)}
                                </Select>
                            </Form.Item>

                            <Form.Item<FeeLadderModel>
                                label="Giá khác"
                                name="otherPrice"
                            >
                                <Input placeholder="Nhập giá khác" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>

                            <Form.Item<FeeLadderModel>
                                label="Loại bảng giá"
                                name="ladderType"
                                rules={[{ required: true, message: 'Bạn phải chọn Loại bảng giá!' }]}
                            >
                                <Select showSearch placeholder="Chọn" style={{width: INPUT_WIDTH}}>
                                    {FEE_LADDER_TYPES.map(type => <Select.Option key={type} value={type}>{feeLadderTypePipe(type)}</Select.Option>)}
                                </Select>
                            </Form.Item>

                            <Form.Item<FeeLadderModel>
                                label="Số nước tối đa theo số khẩu (m3)"
                                name="maxIndex"
                            >
                                <Input placeholder="Nhập số nước tối đa" style={{width: INPUT_WIDTH}}></Input>
                            </Form.Item>

                        </div>
                    </Form>
                </div>
                <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
            </Modal>
        </ConfigProvider>
    )
}